import type { TrackController } from '../track/trackController'
import { lanePositions } from '../../config/constants'
import type { PathObstaclePlacement } from '../../config/obstaclePositions'
import type { HitAnimationCategory } from '../../../stores/runStateTypes'

type ObstacleGridCells = readonly [
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
]

const obstacleHitboxCube = { x: 0.25, y: 0.3, z: 0.25 }

const obstacleRowCenterY = [1.3, 0.3] as const

const obstacleGridByType: Record<number, ObstacleGridCells> = {
    // top-left, top-center, top-right, bottom-left, bottom-center, bottom-right
    1: [true, false, false, true, false, false],
    2: [false, false, false, true, true, false],
    3: [true, true, false, true, true, false],
    4: [false, false, false, true, true, true],
    5: [false, true, false, true, true, false],
    6: [false, false, false, true, false, false],
    7: [true, true, false, true, false, false],
}

const obstacleGridDefault: ObstacleGridCells = [
    false,
    false,
    false,
    true,
    false,
    false,
]

export const PLAYER_HITBOX_HALF = { x: 0.2, y: 0.5, z: 0.4 }

export type ObstacleHitData = {
    index: number
    trackPosition: number
    laneX: number
    centerY: number
    hitAnimationCategory: HitAnimationCategory
    hitbox: { x: number; y: number; z: number }
}

export type ObstacleCollisionData = {
    obstacles: ObstacleHitData[]
}

export type ObstacleCollisionMatch = {
    index: number
    stopTrackPosition: number
    hitAnimationCategory: HitAnimationCategory
}

export function buildObstacleCollisionData(
    placements: readonly PathObstaclePlacement[],
    _trackController: TrackController,
): ObstacleCollisionData {
    const obstacles: ObstacleHitData[] = []

    placements.forEach((placement, index) => {
        const grid = obstacleGridByType[placement.type] ?? obstacleGridDefault
        const laneDirection = placement.flip === 1 ? -1 : 1
        const hitAnimationCategory = grid.some(
            (active, cellIndex) => active && cellIndex < 3,
        )
            ? 'upperObstacle'
            : 'lowerObstacle'

        grid.forEach((active, cellIndex) => {
            if (!active) return

            const rowIndex = cellIndex < 3 ? 0 : 1
            const columnIndex = cellIndex % 3
            const occupiedTrack = placement.track + columnIndex * laneDirection
            const laneX = lanePositions[occupiedTrack]

            if (laneX === undefined) {
                return
            }

            obstacles.push({
                index,
                trackPosition: placement.position,
                laneX,
                centerY: obstacleRowCenterY[rowIndex],
                hitAnimationCategory,
                hitbox: obstacleHitboxCube,
            })
        })
    })

    return { obstacles }
}

// Track-Distanz (Z-Achse lokal): wie weit ist das Obstacle entfernt?
// Lane-X (X-Achse lokal): auf welcher Lane ist der Spieler vs. das Obstacle?
// Jump-Y (Y-Achse lokal): schneidet der Spieler eine der Hitbox-Cubes?

export function checkObstacleCollision(
    playerTrackPosition: number,
    playerLaneX: number,
    playerJumpOffset: number,
    collisionData: ObstacleCollisionData,
): ObstacleCollisionMatch | null {
    let collisionMatch: ObstacleCollisionMatch | null = null

    for (const obstacle of collisionData.obstacles) {
        // Cells for a placement are contiguous, so once we move past the first
        // colliding obstacle we can return it. Animation category is derived
        // from the obstacle layout, not the specific cube that was hit.
        if (collisionMatch && obstacle.index !== collisionMatch.index) {
            return collisionMatch
        }

        const trackDelta = Math.abs(playerTrackPosition - obstacle.trackPosition)
        const collisionDistance = PLAYER_HITBOX_HALF.z + obstacle.hitbox.z
        if (trackDelta > collisionDistance) continue

        const laneDelta = Math.abs(playerLaneX - obstacle.laneX)
        if (laneDelta >= PLAYER_HITBOX_HALF.x + obstacle.hitbox.x) continue

        const playerBottom = playerJumpOffset
        const playerTop = playerJumpOffset + PLAYER_HITBOX_HALF.y * 2
        const obstacleBottom = obstacle.centerY - obstacle.hitbox.y
        const obstacleTop = obstacle.centerY + obstacle.hitbox.y

        if (playerTop <= obstacleBottom || playerBottom >= obstacleTop) continue

        if (!collisionMatch) {
            collisionMatch = {
                index: obstacle.index,
                stopTrackPosition: obstacle.trackPosition - collisionDistance,
                hitAnimationCategory: obstacle.hitAnimationCategory,
            }
        }
    }

    return collisionMatch
}
